import { DEFAULT_ROWS_PER_PAGE } from "../constants/DataTable";
import { isObjectEmpty } from "./object";
import qs from 'qs';

export const getQueryString = ({baseUrl, keyword, offset, limit, sort, dateRange, amountRange, optionalParams}) => {
    const query = {
        ...((sort && !isObjectEmpty(sort)) ? {
            sort_col: sort.col,
            sort_direction: sort.direction
        } : {}),
        ...{
            limit: limit || DEFAULT_ROWS_PER_PAGE,
            offset: offset,
        },
        ...(keyword ? { keyword: keyword} : {}),
        ...(dateRange && dateRange.length === 2 ? {date_range: `[${dateRange[0]},${dateRange[1]}]` } : {}),
        ...(amountRange && amountRange.length === 2 ? {amount_range: `[${amountRange[0]},${amountRange[1]}]` } : {}),
        ...(typeof optionalParams === 'object' ? optionalParams : {}),
    };
    const queryString = qs.stringify(query);
    const optionalParamsString = typeof optionalParams === 'string' ? optionalParams : ''; //We should eventually remove the string representation.

    return `/${baseUrl}?${queryString}${optionalParamsString}`;
}

/**
 * Build search query parameters from keyword and filters for the global search API.
 * Combines inline filter syntax (is:, status:, amount:, date:) with the keyword,
 * and returns customer filters as separate URL parameters.
 *
 * @param {string} keyword - The search keyword
 * @param {Object} filters - Filter state object
 * @param {Object|null} filters.amount - Amount filter { min: cents, max: cents }
 * @param {Object|null} filters.date - Date filter { start: 'yyyy-mm-dd', end: 'yyyy-mm-dd' }
 * @param {Array} filters.customers - Array of selected customers/groups with _type field
 * @param {string[]} filters.invoice - Invoice status filters array
 * @param {string[]} filters.transaction - Transaction type filters array
 * @returns {Object} Query parameters object for URL building
 */
export const buildSearchQuery = (keyword, filters = {}) => {
    const parts = [];

    // Invoice filters: is:invoice status:paid, status:overdue, etc.
    if (filters.invoice && filters.invoice.length > 0) {
        parts.push('is:invoice');
        filters.invoice.forEach(s => parts.push(`status:${s}`));
    }

    // Transaction filters handled via dedicated transaction_type parameter (not is: syntax)
    // See params.transaction_type below

    // Amount filter: amount:5000, amount:>5000, amount:5000..10000
    if (filters.amount) {
        const { min, max } = filters.amount;
        if (min && max) {
            parts.push(`amount:${min}..${max}`);
        } else if (min) {
            parts.push(`amount:>=${min}`);
        } else if (max) {
            parts.push(`amount:<=${max}`);
        }
    }

    // Date filter: date:2024-01-01, date:>2024-01-01, date:2024-01-01..2024-03-31
    if (filters.date) {
        const { start, end } = filters.date;
        if (start && end) {
            parts.push(`date:${start}..${end}`);
        } else if (start) {
            parts.push(`date:>=${start}`);
        } else if (end) {
            parts.push(`date:<=${end}`);
        }
    }

    // Combine filter syntax with keyword
    const keywordWithFilters = parts.length
        ? `${parts.join(' ')} ${keyword || ''}`.trim()
        : (keyword || '');

    // Build URL params object
    const params = {};
    if (keywordWithFilters) {
        params.keyword = keywordWithFilters;
    }

    // Handle multiple customers/groups using customer_filter parameter
    // Format: customer_filter=customer:1,customer:2,group:3
    if (filters.customers && filters.customers.length > 0) {
        const customerFilters = filters.customers.map(item => {
            const type = item._type === 'group' ? 'group' : 'customer';
            return `${type}:${item.id}`;
        });
        params.customer_filter = customerFilters.join(',');
    }

    // Transaction type filter: transaction_type=ach,card,check,wire,other_payment
    // Use dedicated parameter instead of is: syntax
    if (filters.transaction && filters.transaction.length > 0) {
        params.transaction_type = filters.transaction.join(',');
    }

    return params;
};

/**
 * Check if any filters are currently active
 * @param {Object} filters - Filter state object
 * @returns {boolean} True if any filter is active
 */
export const hasActiveFilters = (filters = {}) => {
    return !!(
        (filters.invoice && filters.invoice.length > 0) ||
        (filters.transaction && filters.transaction.length > 0) ||
        filters.amount ||
        filters.date ||
        (filters.customers && filters.customers.length > 0)
    );
};
